import {
  emitNoteContentPasted,
  reportPasteFlavorDataFailure,
} from '@/components/tiptap/shared/emit-note-content-pasted';
import { resolveClipboardPlainText } from '@/helpers/copyHelpers/plainTextClipboard';
import { restoreParagraphsFromBreakLines } from '@/helpers/markdownToHtml';
import { getSelectionWordCounts } from '@/helpers/wordCount';
import {
  CopyMethod,
  SessionCopiedFormat,
  type SessionCopySource,
} from '@/models/analytics/features/sessions';
import { createDropTargetTracker, normalizePastedSections } from '@heidi/editor/core';
import type { ResolvedPos, Slice } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { Extension, type Editor } from '@tiptap/react';
import { emitSessionCopied } from '../../analytics/emit-session-copied';
import { buildClipboardPayload, type ClipboardPayload } from './build-clipboard-payload';
import {
  classifyPasteFlavor,
  handleVsCodePaste,
  parseMarkdownPastedText,
  pastedContentLength,
  sanitizePastedHtml,
} from './paste';

export { buildClipboardPayload, type ClipboardPayload };

/**
 * Write the selection to `event.clipboardData` synchronously. The synchronous
 * `setData` is required because the macOS WKWebView context-menu "Copy" has no
 * transient user activation, so async `navigator.clipboard.write()` fails
 * silently there. Works for both keyboard shortcut and context menu.
 *
 * Returns `false` (so the caller does not `preventDefault`) when there is
 * nothing to write, falling back to the browser default.
 */
const writeSelectionToClipboard = (
  editor: Editor,
  view: EditorView,
  event: ClipboardEvent,
  copySource: SessionCopySource
): boolean => {
  if (!event.clipboardData) {
    return false;
  }

  const payload = buildClipboardPayload(editor, view);
  if (!payload) {
    return false;
  }

  event.clipboardData.setData('text/html', payload.html);
  event.clipboardData.setData(
    'text/plain',
    resolveClipboardPlainText({ markdown: payload.markdown })
  );

  // Twin of the vMarkdown ClipboardTextSerializer firing — keyboard/context-menu
  // copies (and cuts) were invisible in markdown-native until now.
  const { from, to } = view.state.selection;
  const { selectedWordCount, totalWordCount } = getSelectionWordCounts(
    view.state.doc,
    from,
    to
  );
  emitSessionCopied({
    // Shortcut copy is verbatim: ⌘C / cut / context-menu write the highlighted
    // fragment as-is. format is therefore a constant on SHORTCUT, not a user
    // choice — unlike the note-level Copy button and (when flagged on) the
    // text-menu path.
    format: SessionCopiedFormat.FORMAT_WITH_LISTS,
    copyMethod: CopyMethod.SHORTCUT,
    selectedWordCount,
    totalWordCount,
    copySource,
  });

  return true;
};

/**
 * Markdown-native clipboard extension: copy/cut a selection (including tables)
 * as clean GFM markdown, paste plain text through the canonical markdown
 * pipeline and pasted HTML through the sanitizer in `./paste`. Standalone twin
 * of the vMarkdown `ClipboardTextSerializer` — no legacy regex pipeline; the
 * only copy-side pipeline helper it shares is `resolveClipboardPlainText`, for
 * the visual `text/plain` fallback.
 */
export type ClipboardSerializerOptions = {
  copySource: SessionCopySource;
};

export const ClipboardSerializer = Extension.create<ClipboardSerializerOptions>({
  name: 'markdown-native-clipboard',

  addOptions() {
    return {
      copySource: 'editor_clipboard',
    };
  },

  addProseMirrorPlugins() {
    const { editor } = this;
    const dropTarget = createDropTargetTracker();
    const copySource = this.options?.copySource ?? 'editor_clipboard';

    return [
      new Plugin({
        key: new PluginKey(this.name),
        props: {
          // Restore marked paragraph-breaks before sanitizing so a note/section
          // copy round-trips as separate paragraphs, not one hard-break block.
          transformPastedHTML: (html) =>
            sanitizePastedHtml(restoreParagraphsFromBreakLines(html)),

          // Both paste paths meet here as a parsed `Slice` — the one place the
          // HTML flavor's bare `<h1>` and the plain-text flavor's `sectionNode`
          // reconcile. Drops arrive here too, judged at their own target.
          transformPasted: (slice, view) =>
            normalizePastedSections(slice, dropTarget.resolveContext(view)),

          // VS Code copies parse as markdown like any other text paste rather
          // than landing as one typed code block.
          handlePaste: (view, event) => handleVsCodePaste(editor, view, event),

          // The prop type demands a `Slice`, but ProseMirror treats a falsy
          // return as unhandled and runs its default plain-text paste — the
          // cast lets `null` express that fallback.
          clipboardTextParser: (
            text: string,
            _$context: ResolvedPos,
            _plain: boolean,
            view: EditorView
          ) => parseMarkdownPastedText(editor, view, text) as Slice,

          handleDOMEvents: {
            drop: dropTarget.captureDrop,

            // Observe-only paste telemetry: one event per paste, classified
            // by clipboard flavor. Never handles the paste itself.
            paste: (_view, event) => {
              const data = event.clipboardData;
              if (data) {
                emitNoteContentPasted({
                  editorType: 'markdown_native',
                  pasteFlavor: classifyPasteFlavor(
                    data,
                    reportPasteFlavorDataFailure('markdown_native')
                  ),
                  contentLength: pastedContentLength(data),
                });
              }
              return false;
            },

            copy: (view, event) => {
              if (!writeSelectionToClipboard(editor, view, event, copySource)) {
                return false;
              }
              event.preventDefault();
              return true;
            },

            cut: (view, event) => {
              if (!writeSelectionToClipboard(editor, view, event, copySource)) {
                return false;
              }
              event.preventDefault();
              view.dispatch(view.state.tr.deleteSelection());
              return true;
            },
          },
        },
      }),
    ];
  },
});
